import { html, el, trusted } from '../html.js';
import { CLOSE } from '../icons.js';
import { screen, progress, watchLeaving } from '../shell.js';
import { api, upload } from '../api.js';
import { go } from '../router.js';
import { state } from '../state.js';
import { stars, starInput } from '../stars.js';
import { confirmAction } from '../dialog.js';
import { formatDate } from '../lib/format.js';
import { mapsLink } from '../lib/maps-link.js';
import { preparePhotos, photoForm } from '../lib/photos.js';

export async function placeView({ id }) {
  // Read before the first await: a later navigation changes both.
  const opened = state.nav;
  const leaving = watchLeaving();
  // As opened (even as /places/05), so that showing this page again is a same-path navigation.
  const { here, left, tell } = leaving;
  const p = await api('GET', `/api/places/${id}`);
  const me = state.user;
  const mine = [p.mainRating, ...p.otherRatings].find(r => r && r.userId === me.id);
  const href = mapsLink(p, navigator);
  const external = href.startsWith('https://www.openstreetmap.org/');
  const rating = (r, className) => html`<div class="${className}">
    ${stars(r.stars)}
    <p class="meta">${r.username}</p>
    ${r.review ? html`<p>${r.review}</p>` : ''}
    ${me.isOwner && r.userId !== me.id ? html`<button class="destructive" type="button" data-delete-rating="${r.userId}">Delete</button>` : ''}
  </div>`;

  // The page pushes in from the right only when it is opened from another screen.
  const s = screen({ back: true, className: opened === 'push' ? 'place-screen push-in' : 'place-screen' });
  s.body.append(el(html`<article class="place">
    <div class="hero">
      <div class="photos" ${p.photos.length ? html`tabindex="0"` : ''} aria-label="Photos">${p.photos.length
        ? p.photos.map(ph => html`<img src="/api/photos/${ph.id}" alt="">`)
        : html`<div class="no-photo">No photos yet</div>`}</div>
      <div class="scrim">
        <div class="dots" aria-hidden="true">${p.photos.map((_, i) =>
          html`<svg class="${i === 0 ? 'on' : ''}" viewBox="0 0 8 8" width="8" height="8"><circle cx="4" cy="4" r="3.5"/></svg>`)}</div>
        <h1>${p.name}</h1>
        <p class="byline meta">${p.addedBy}, ${formatDate(p.createdAt)}</p>
      </div>
    </div>
    <div class="stack">
      ${p.gettingThere ? html`<div class="getting-there"><p class="meta">Getting there</p><p>${p.gettingThere}</p></div>` : ''}
      ${p.description ? html`<p class="description">${p.description}</p>` : ''}
      ${p.mainRating ? rating(p.mainRating, 'main-rating') : ''}
      ${p.otherRatings.length ? html`<details class="other-ratings">
        <summary>Other ratings (${p.otherRatings.length})</summary>
        ${p.otherRatings.map(r => rating(r, 'rating'))}
      </details>` : ''}
      ${p.address ? html`<p class="address">${p.address}</p>` : ''}
      <a class="primary open-in-maps" href="${href}" ${external ? html`target="_blank" rel="noopener"` : ''}>Open in maps</a>
      <details class="manage">
        <summary class="secondary">Edit</summary>
        <div class="manage-body">
          <form class="my-rating stack-tight" novalidate>
            ${starInput('stars', mine?.stars ?? 0)}
            <label class="field"><span>Review</span><textarea name="review" maxlength="500">${mine?.review ?? ''}</textarea></label>
            <p class="error" role="alert" hidden></p>
            <button class="secondary" type="submit">Save rating</button>
            ${mine ? html`<button class="destructive" type="button" data-delete-rating="${me.id}">Delete rating</button>` : ''}
          </form>
          <div class="photo-actions stack-tight"></div>
          <a class="secondary" href="/places/${p.id}/edit">Edit details</a>
          <button class="destructive" type="button" data-delete-place>Delete place</button>
        </div>
      </details>
    </div>
  </article>`));

  const root = s.body;
  const strip = root.querySelector('.photos');
  const dots = [...root.querySelectorAll('.dots svg')];
  strip.addEventListener('scroll', () => {
    const at = Math.round(strip.scrollLeft / strip.clientWidth);
    dots.forEach((d, i) => d.classList.toggle('on', i === at));
  });

  // A tap (a swipe sends no click) opens the photos whole, on the one tapped.
  let viewer = null;
  function openViewer(index) {
    viewer = el(html`<dialog class="viewer" aria-label="Photos">
      <div class="viewer-photos">${p.photos.map(ph => html`<img src="/api/photos/${ph.id}" alt="">`)}</div>
      <form method="dialog"><button class="icon-btn" aria-label="Close">${trusted(CLOSE)}</button></form>
      ${p.photos.length > 1 ? html`<p class="viewer-count" aria-live="polite">${index + 1} / ${p.photos.length}</p>` : ''}
    </dialog>`);
    const list = viewer.querySelector('.viewer-photos');
    const count = viewer.querySelector('.viewer-count');
    count && list.addEventListener('scroll', () => {
      count.textContent = `${Math.round(list.scrollLeft / list.clientWidth) + 1} / ${p.photos.length}`;
    });
    viewer.addEventListener('close', () => { viewer.remove(); viewer = null; strip.focus(); });
    document.body.append(viewer);
    viewer.showModal();
    list.children[index].scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'instant' });
  }
  strip.addEventListener('click', e => {
    const at = [...strip.children].indexOf(e.target.closest('img'));
    if (at >= 0) openViewer(at);
  });
  strip.addEventListener('keydown', e => {
    if (e.key !== 'Enter' || e.target !== strip || !p.photos.length) return;
    e.preventDefault(); // the same keypress would press Close, which the viewer focuses
    openViewer(Math.round(strip.scrollLeft / strip.clientWidth));
  });

  const form = root.querySelector('.my-rating');
  const problem = form.querySelector('.error');
  form.addEventListener('submit', async e => {
    e.preventDefault();
    const picked = form.querySelector('input[name=stars]:checked');
    problem.hidden = !!picked;
    problem.textContent = 'Pick 1 to 5 stars.';
    if (!picked) return;
    try {
      await api('PUT', `/api/places/${p.id}/rating`, { stars: Number(picked.value), review: form.elements.review.value });
      go(here, 'Rating saved.');
    } catch (x) {
      problem.textContent = x.message;
      problem.hidden = false;
    }
  });

  const act = (question, word, path, after) => async () => {
    if (!(await confirmAction(question, word))) return;
    try {
      await api('DELETE', path);
      after();
    } catch (x) { s.status(x.message); }
  };
  root.querySelectorAll('[data-delete-rating]').forEach(b => b.addEventListener('click',
    act("Delete this rating? This can't be undone.", 'Delete', `/api/places/${p.id}/ratings/${b.dataset.deleteRating}`, () => go(here, 'Rating deleted.'))));
  root.querySelector('[data-delete-place]').addEventListener('click',
    act("Delete this place and its photos? This can't be undone.", 'Delete', `/api/places/${p.id}`, () => go('/', 'Place deleted.', { replace: true })));

  const manage = root.querySelector('.manage');
  const actions = root.querySelector('.photo-actions');
  actions.append(el(html`<div class="stack-tight">
    <input type="file" accept="image/*,.heic,.heif" multiple hidden>
    <div class="photo-errors"></div>
    <p class="error" role="alert" data-upload-error hidden></p>
    <button class="secondary" type="button" data-add-photos>Add photos</button>
    <button class="secondary" type="button" data-retry hidden>Try again</button>
  </div>`));
  const picker = actions.querySelector('input[type=file]');
  const photoErrors = actions.querySelector('.photo-errors');
  const uploadError = actions.querySelector('[data-upload-error]');
  const addButton = actions.querySelector('[data-add-photos]');
  const retry = actions.querySelector('[data-retry]');
  let pending = null; // the last pick, ready to send: { form, errors }
  let busy = false; // from a pick until its upload has settled: no second pick, no second Try again
  // The member left this page: what happens next is told on the screen they went to.
  // Going back (or anywhere) closes the viewer: it lies outside the screen.
  s.el.cleanup = () => { viewer?.close(); leaving.cleanup(); };

  async function send(bar) {
    retry.hidden = true;
    uploadError.hidden = true;
    try {
      await upload(`/api/places/${p.id}/photos`, pending.form, bar.set);
      const text = ['Photos added.', ...pending.errors].join(' ');
      if (left()) tell(text);
      else go(here, text);
    } catch (x) {
      if (left()) {
        tell(x.message);
      } else {
        uploadError.textContent = x.message;
        uploadError.hidden = false;
        manage.open = true;
        retry.hidden = false;
      }
    }
  }
  // The copper line runs from the pick (preparing can take a while) until the upload settles.
  async function start(work) {
    busy = addButton.disabled = retry.disabled = true;
    const bar = progress();
    try {
      await work(bar);
    } finally {
      bar.done();
      busy = addButton.disabled = retry.disabled = false;
    }
  }
  addButton.addEventListener('click', () => picker.click());
  retry.addEventListener('click', () => start(send));
  picker.addEventListener('change', async () => {
    const files = [...picker.files];
    picker.value = '';
    if (busy || !files.length) return;
    await start(async bar => {
      const { good, errors } = await preparePhotos(files);
      photoErrors.replaceChildren(...errors.map(m => el(html`<p class="error" role="alert">${m}</p>`)));
      if (errors.length) manage.open = true;
      if (!good.length) {
        if (left()) tell(errors.join(' '));
        return;
      }
      pending = { form: photoForm(good), errors };
      await send(bar);
    });
  });

  return s.el;
}
